import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { MouseEvent, ReactNode, useMemo } from 'react';
import styled from 'styled-components';

import { EligibleCardsSort } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelXS } from '@sorare/core/src/atoms/typography';
import { PrintablePlayerSortName } from '@sorare/core/src/contexts/config';
import {
  PlayerSearchViewAttributes,
  StatsOnboarding,
} from '@sorare/core/src/lib/playerSearchView';

import { PlayerSearchHelp } from 'searchCommonPlayer/PlayerSearchHelp';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

const Wrapper = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--c-nd-800);
  opacity: 0.5;
  transition: opacity 0.2s;
  & > *:last-child {
    opacity: 0;
  }
  &:hover,
  &.active {
    opacity: 1;
    & > *:last-child {
      opacity: 1;
    }
  }
`;

const SortIconWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  position: absolute;
  left: 100%;
  top: 0;
  bottom: 0;
  padding-left: var(--half-unit);
`;

const isPrintablePlayerSortName = (
  value: PrintablePlayerSortName | EligibleCardsSort
): value is PrintablePlayerSortName => {
  return typeof value === 'string';
};

const isPrintablePlayerSortNames = (
  values: (PrintablePlayerSortName | EligibleCardsSort)[]
): values is PrintablePlayerSortName[] => {
  return typeof values[0] === 'string';
};

const COMBINED_SORTS_LIMIT = 3;

const addSort = <T extends PrintablePlayerSortName | EligibleCardsSort>(
  sorts: T[],
  sort: T
): T[] => {
  if (!isPrintablePlayerSortName(sort) || !isPrintablePlayerSortNames(sorts)) {
    return [sort];
  }

  const [, valueName] = sort.split('-');
  let values = sorts.map(_sort => {
    const [, sortName] = _sort.toString().split('-');
    if (sortName === valueName) {
      // a sort on the same attribute is already applied, toggle the direction
      return sort;
    }
    return _sort;
  });
  // if after trying to toggle the refinement, the value is not found, add it at the end
  if (!values.includes(sort)) {
    if (values.length >= COMBINED_SORTS_LIMIT) {
      values = values.slice(0, COMBINED_SORTS_LIMIT - 1);
    }
    values = [...values, sort];
  }

  return values;
};

const getRefineIndexes = <
  T extends PrintablePlayerSortName | EligibleCardsSort,
>(
  sorts: T[],
  indexes: {
    asc: T;
    desc: T;
  }
) => {
  const matchingSorts = sorts
    .map(_sort => {
      if (isPrintablePlayerSortName(_sort)) {
        return { asc: _sort === indexes.asc, desc: _sort === indexes.desc };
      }
      if (
        !isPrintablePlayerSortName(indexes.asc) &&
        !isPrintablePlayerSortName(indexes.desc)
      ) {
        return {
          asc:
            _sort.type === indexes.asc.type &&
            _sort.direction === indexes.asc.direction,
          desc:
            _sort.type === indexes.desc.type &&
            _sort.direction === indexes.desc.direction,
        };
      }
      return null;
    })
    .filter(Boolean);

  return {
    ascRefineIndex: matchingSorts.findIndex(({ asc }) => asc) + 1,
    descRefineIndex: matchingSorts.findIndex(({ desc }) => desc) + 1,
  };
};

type Props<T> = {
  children: ReactNode;
  indexes: {
    asc: T;
    desc: T;
  };
  sorts: T[];
  refine: (sorts: T[]) => void;
  onboarding?: StatsOnboarding;
  ascFirst?: boolean;
};

export const SortTable = <
  T extends PrintablePlayerSortName | EligibleCardsSort,
>({
  children,
  indexes,
  sorts,
  refine,
  ascFirst,
  onboarding,
}: Props<T>) => {
  const { ascRefineIndex, descRefineIndex } = getRefineIndexes(sorts, indexes);
  const isAscRefine = ascRefineIndex > 0;
  const isDescRefine = descRefineIndex > 0;
  const isRefined = isAscRefine || isDescRefine;

  const { statsManager, sawOnboarding } = useManagePlayerSearchView();
  const icon = useMemo(() => {
    if (isAscRefine) return faCaretUp;
    if (isDescRefine) return faCaretDown;
    return ascFirst ? faCaretUp : faCaretDown;
  }, [isAscRefine, isDescRefine, ascFirst]);

  const shouldShowOnboarding =
    statsManager[PlayerSearchViewAttributes.ONBOARDING] === onboarding;

  const onClick = (e: MouseEvent<HTMLElement>) => {
    if (shouldShowOnboarding) sawOnboarding(onboarding);

    if (!isRefined) {
      const firstRefineSort = ascFirst ? indexes.asc : indexes.desc;
      refine(e.shiftKey ? addSort(sorts, firstRefineSort) : [firstRefineSort]);
      return;
    }
    if (indexes?.asc && !isAscRefine) {
      refine(e.shiftKey ? addSort(sorts, indexes.asc) : [indexes.asc]);
      return;
    }
    if (indexes?.desc && !isDescRefine) {
      refine(e.shiftKey ? addSort(sorts, indexes.desc) : [indexes.desc]);
    }
  };

  return (
    <ConditionalWrapper
      wrap={!!onboarding && shouldShowOnboarding}
      Wrapper={({ children: c }: { children: ReactNode }) => (
        <PlayerSearchHelp placement="top" step={onboarding!}>
          {c}
        </PlayerSearchHelp>
      )}
    >
      <Wrapper className={isRefined ? 'active' : ''} onClick={onClick}>
        {children}
        <SortIconWrapper>
          <FontAwesomeIcon icon={icon} size="xs" />
          {ascRefineIndex > 1 && <LabelXS>{ascRefineIndex}</LabelXS>}
          {descRefineIndex > 1 && <LabelXS>{descRefineIndex}</LabelXS>}
        </SortIconWrapper>
      </Wrapper>
    </ConditionalWrapper>
  );
};
